import React, { FunctionComponent, useEffect, useState } from 'react';
import produce from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './RequestedApprovers.scss';
import { SearchDropdown, SearchItem } from 'components';
import { IconButton} from '@cfa/react-components';
import { AsyncStatus } from 'enums';
import { IUser } from 'interfaces';
import { FETextSearcher, ISearcherOnSearchResponse, parseAxiosError, UsersService } from 'services';

interface IRequestedApproversProps {
    approverIDs: string[],
    disabled: boolean,
    onChange: (newApproverIDs: string[]) => void,
}

interface IRequestedApproversState {
    allApproversByID: { [id: string]: IUser },
    feTextSearcher?: FETextSearcher<IUser>,
    loadError?: string,
    loadStatus: AsyncStatus,
}

// Section for requesting approvers
export const RequestedApprovers: FunctionComponent<IRequestedApproversProps> = function RequestedApprovers(props) {
    const approverIDs = props.approverIDs || [];
    const { disabled, onChange } = props;

    const [state, setState] = useState<IRequestedApproversState>({
        allApproversByID: {},
        loadStatus: AsyncStatus.Loading,
    });
    const { allApproversByID, feTextSearcher, loadError, loadStatus } = state;

    // Load the list of all approvers. It's a short list, so we'll do client side searching for our dropdown.
    useEffect(() => {
        UsersService.getApprovers()
            .then((approvers) => {
                setState(produce(draft => {
                    draft.loadStatus = AsyncStatus.Success;

                    // Sort by name, case-insensitive
                    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
                    approvers.sort((a, b) => collator.compare(a.name, b.name));

                    // Initialize a frontend text searcher
                    draft.feTextSearcher = new FETextSearcher<IUser>(approvers, (user) => user.name);

                    // Store approvers in a map for easy lookups.
                    draft.allApproversByID = {}
                    for (const user of approvers) {
                        draft.allApproversByID[user.snowId] = user;
                    }
                }));
            })
            .catch((err) => {
                setState(produce(draft => {
                    draft.loadStatus = AsyncStatus.Error;
                    draft.loadError = 'Error loading approvers: ' + parseAxiosError(err);
                }));
            });
    }, []);

    function onSearch(query: string, offset: number): ISearcherOnSearchResponse<IUser> {
        return new Promise((resolve, reject) => {
            // Use our frontend text searcher to filter the list
            const results = feTextSearcher.search(query);

            resolve({
                canLoadMore: false,
                results: results,
            });
        })
    }

    function onRemoveApprover(approverIDToRemove: string) {
        const newApproverIDs = approverIDs.filter(id => id !== approverIDToRemove);
        onChange(newApproverIDs)
    }

    function onChangeDropdownVal(selectedUser: IUser) {
        if (!selectedUser) return;

        // Add the user id to our list and unique the list
        let newApproverIDs = [...approverIDs, selectedUser.snowId];
        newApproverIDs = [...new Set(newApproverIDs)];

        onChange(newApproverIDs);
    }

    return (
        <div className="requestedApprovers">
            {loadStatus === AsyncStatus.Loading && (
                <span>Loading approvers...</span>
            )}
            {loadStatus === AsyncStatus.Error && (
                <span className="requestedApprovers_error">{loadError}</span>
            )}
            {loadStatus === AsyncStatus.Success && (<>
                {approverIDs.length > 0 && (
                    <ul>
                        {approverIDs.map(id => (
                            <li key={id} className="requestedApprovers_item">
                                <div className="requestedApprovers_itemInner">
                                    <span className="requestedApprovers_itemLabel ellipsis">
                                        {allApproversByID[id]?.name || ('Missing approver: ' + id)}
                                    </span>
                                    {!disabled && (
                                        <IconButton
                                            className="requestedApprovers_removeButton"
                                            disabled={disabled}
                                            onClick={() => onRemoveApprover(id)}
                                            size="sm"
                                        >
                                            <FontAwesomeIcon icon="times" />
                                        </IconButton>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                <SearchDropdown<IUser>
                    className="requestedApprovers_dropdown"
                    disabled={disabled}
                    dontDebounce={true}
                    getOptionValue={(user) => user.snowId}
                    onChange={onChangeDropdownVal}
                    onSearch={onSearch}
                    placeholder="Add approver"
                    renderOption={(user) => <SearchItem label={user.name} />}
                    value={undefined}
                />
            </>)}
        </div>
    )
}